import * as React from "react";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { connect } from "react-redux";
import moment, { Moment } from "moment";
import { withRouter } from "react-router";

import withGranted, { IWithGrantedProps } from "../HOC/WithGranted";
import { InvoicePricingErrorDto } from "../../service-proxies";
import { createStructuredSelector } from "reselect";
import { IGlobalStore } from "../../_reducers/reducers";
import { IShortLocationInfo } from "../../_types/common";
import * as actionsSearch from "../../_actions/search";
import * as layoutActions from "../../_actions/layout";
import * as invoicesApi from "../../_api/invoices";
import * as vendorApi from "../../_api/vendor";
import { notifySwagger, notify } from "../../_actions/notification";

import {
  DatePicker,
  Table,
  LocationSelectorComponent,
  Pagination,
  MainContainer,
  RunningLineLoader,
  Skeleton,
  Button,
  Article
} from "../../components";
import { IFlatLocationOption } from "../../_reducers/current-user";
import { clientUnitFilterOptions } from "../../_utils/clientUnitFilter";
import { formatPrice } from "../../_utils/prices";

const Position = {
  Container: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,

  Body: styled.div`
    height: 100%;
    flex: 1 1 100%;
  `,

  Footer: styled.div`
    flex: 0 0;
  `,

  Head: styled<{ className?: string }>(({ className, children }) => (
    <header className={className}>
      <div>{children}</div>
    </header>
  ))`
    padding: 20px;
    > div {
      margin: -7.5px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
    }
  `,

  HeadElement: styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    & > * {
      margin: 7.5px;
    }
  `,

  Filter: styled.div`
    width: 260px;
  `,

  Toolbar: styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    justify-content: flex-end;
    > * {
      margin-left: 10px;
    }
  `
};

interface IGetPricingErrorsParams {
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  sorting?: string;
  skip?: number;
  perPage?: number;
}

interface IPricingErrorsListProps
  extends IWithGrantedProps,
    IConnectedProps,
    IConnectedActions {}

interface IPricingErrorsListState {
  sorting: string;
  skip: number;
  pricingErrorsStatus: string;
  pricingErrors?: InvoicePricingErrorDto[];
  totalCount: number;
  clientUnitFilter: number | null;
  vendorFilter: number | null;
  startDate: Moment | null;
  endDate: Moment | null;
  focusedInput: string;
  isExporting: boolean;
  isResolving: { [id: number]: boolean };
  vendors: Array<{ id: number; name: string }>;
  vendorsLoaded: boolean;
}

class PricingErrorsList extends React.Component<
  IPricingErrorsListProps,
  IPricingErrorsListState
> {
  static renderPreloader(perPage: number, status: string): React.Component[] {
    const rows = [];
    const elementsCount = status === "loading" ? perPage : 0;

    for (let i = 0; i < elementsCount; i++) {
      rows.push(
        <Table.Row
          key={i}
          rows={perPage}
          cells={[
            <Skeleton.Line key={`pricing-errors-preloader-${i}-0`} size="xs" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-1`} size="sm" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-2`} size="sm" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-3`} size="sm" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-4`} size="sm" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-5`} size="xs" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-6`} size="xs" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-7`} size="sm" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-8`} size="sm" />,
            <Skeleton.Line key={`pricing-errors-preloader-${i}-9`} size="sm" />
          ]}
        />
      );
    }
    return rows;
  }

  constructor(props) {
    super(props);

    this.state = {
      sorting: "dateRangeStart DESC",
      skip: 0,
      pricingErrorsStatus: "",
      totalCount: 0,
      clientUnitFilter: null,
      vendorFilter: null,
      startDate: null,
      endDate: null,
      focusedInput: null,
      isExporting: false,
      isResolving: {},
      vendors: [],
      vendorsLoaded: false
    };

    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleSortChanged = this.handleSortChanged.bind(this);
    this.getPricingErrors = this.getPricingErrors.bind(this);
    this.handleClientFilterChange = this.handleClientFilterChange.bind(this);
    this.handleVendorFilterChange = this.handleVendorFilterChange.bind(this);
    this.handleDateChanged = this.handleDateChanged.bind(this);
    this.handleExportClick = this.handleExportClick.bind(this);
    this.handleResolveClick = this.handleResolveClick.bind(this);

    this.props.setSearch("/invoices/pricing-errors", () => {
      this.setState({ skip: 0 }, () => {
        this.getPricingErrors();
      });
    });
  }

  componentDidMount() {
    // this.loadVendors(); // Vendor filter commented out
    this.getPricingErrors();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.locationInfo.id !== this.props.locationInfo.id) {
      this.setState({ clientUnitFilter: null }, () => {
        this.getPricingErrors();
      });
    }
  }

  loadVendors() {
    vendorApi
      .getVendors(1000, 0, "", "name ASC", false)
      .then(response => {
        const vendors = response.items.map(v => ({
          id: v.id,
          name: v.name
        }));
        this.setState({ vendors, vendorsLoaded: true });
      })
      .catch(e => {
        this.props.notifySwagger(e, "error");
      });
  }

  getPricingErrors(params: IGetPricingErrorsParams = {}) {
    const skip =
      Number.isInteger(params.skip) && params.skip >= 0
        ? params.skip
        : this.state.skip;
    const sort = params.sorting || this.state.sorting;
    const startDate = params.startDate !== undefined ? params.startDate : this.state.startDate;
    const endDate = params.endDate !== undefined ? params.endDate : this.state.endDate;
    const perPage = params.perPage || this.props.perPage;

    let statusStartKey = "loading";
    let statusFinishKey = "loaded";
    if (params.sorting) {
      statusStartKey = "sorting";
      statusFinishKey = "sorted";
    }

    this.setState({ pricingErrorsStatus: statusStartKey });

    console.log('=== getPricingErrors called ===');
    console.log('clientUnitFilter:', this.state.clientUnitFilter);
    console.log('startDate:', startDate);
    console.log('endDate:', endDate);

    invoicesApi
      .getPricingErrors(
        startDate,
        endDate,
        this.state.clientUnitFilter,
        null, // this.state.vendorFilter, // Vendor filter commented out
        skip,
        perPage,
        sort
      )
      .then(response => {
        this.setState({
          pricingErrorsStatus: statusFinishKey,
          pricingErrors: response.items,
          totalCount: response.totalCount
        });
      })
      .catch(e => {
        this.props.notifySwagger(e, "error");
        this.setState({ pricingErrorsStatus: "error" });
      });
  }

  handleChangePage(pageInfo) {
    const { perPage, skip } = pageInfo;

    this.setState({ skip }, () => {
      this.getPricingErrors({ perPage, skip });
      this.props.setPerPage(perPage);
    });
  }

  handleSortChanged(sorting) {
    this.setState({ sorting });
    this.getPricingErrors({ sorting });
  }

  handleClientFilterChange(value: IFlatLocationOption) {
    this.setState({ clientUnitFilter: value?.id || null, skip: 0 }, () => {
      this.getPricingErrors();
    });
  }

  handleVendorFilterChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const vendorId = event.target.value ? parseInt(event.target.value, 10) : null;
    this.setState({ vendorFilter: vendorId, skip: 0 }, () => {
      this.getPricingErrors();
    });
  }

  handleDateChanged(range: { startDate: Moment; endDate: Moment }) {
    const startDate = range.startDate
      ? range.startDate.startOf("day")
      : null;
    const endDate = range.endDate ? range.endDate.startOf("day") : null;

    this.setState({ startDate, endDate, skip: 0 }, () => {
      this.getPricingErrors();
    });
  }

  handleExportClick() {
    this.setState({ isExporting: true });

    invoicesApi
      .exportPricingErrorsToExcel(
        this.state.startDate,
        this.state.endDate,
        this.state.clientUnitFilter,
        null // this.state.vendorFilter // Vendor filter commented out
      )
      .then(() => {
        this.setState({ isExporting: false });
        this.props.notify("Pricing errors exported successfully!", "success");
      })
      .catch(err => {
        this.setState({ isExporting: false });
        if (typeof err === "string") {
          this.props.notify(err, "error");
        } else {
          this.props.notify("Error exporting pricing errors", "error");
        }
      });
  }

  handleResolveClick(errorId: number) {
    this.setState(prevState => ({
      isResolving: { ...prevState.isResolving, [errorId]: true }
    }));

    invoicesApi
      .resolvePricingError(errorId)
      .then(() => {
        this.setState(prevState => ({
          isResolving: { ...prevState.isResolving, [errorId]: false },
          pricingErrors: prevState.pricingErrors.filter(e => e.id !== errorId),
          totalCount: prevState.totalCount - 1
        }));
        this.props.notify("Pricing error resolved successfully!", "success");
      })
      .catch(err => {
        this.setState(prevState => ({
          isResolving: { ...prevState.isResolving, [errorId]: false }
        }));
        this.props.notifySwagger(err, "error");
      });
  }

  render() {
    const hasData = this.state.pricingErrors && this.state.pricingErrors.length > 0;
    const isLoaded = this.state.pricingErrorsStatus === "loaded" || 
                     this.state.pricingErrorsStatus === "sorted";

    return (
      <MainContainer>
        <Position.Container>
          <Position.Head>
            <Position.HeadElement>
              <Article.Text>
                <strong>VENDOR PRICING ERRORS</strong>
              </Article.Text>
            </Position.HeadElement>
            <Position.HeadElement>
              <DatePicker
                compact
                type="range"
                startDateId="startDate"
                endDateId="endDate"
                isOutsideRange={() => false}
                startDate={this.state.startDate}
                endDate={this.state.endDate}
                onDatesChange={this.handleDateChanged}
                focusedInput={this.state.focusedInput}
                keepOpenOnDateSelect
                showPeriods
                onFocusChange={focusedInput => {
                  this.setState({ focusedInput });
                }}
              />
              <Position.Filter>
                <LocationSelectorComponent
                  customFilter={clientUnitFilterOptions}
                  locationOptions={this.props.locationOptions}
                  onLocationChange={this.handleClientFilterChange}
                  selectedLocation={this.state.clientUnitFilter}
                  placeholder="All Clients"
                />
              </Position.Filter>
              {/* Vendor filter commented out */}
              {/* <Position.Filter>
                <Select
                  value={this.state.vendorFilter || ""}
                  onChange={this.handleVendorFilterChange}
                  disabled={!this.state.vendorsLoaded}
                >
                  <option value="">All Vendors</option>
                  {this.state.vendors.map(vendor => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </Select>
              </Position.Filter> */}
              <Button
                onClick={this.handleExportClick}
                primary
                disabled={!hasData || this.state.isExporting}
                preloader={this.state.isExporting}
              >
                Export to Excel
              </Button>
            </Position.HeadElement>
          </Position.Head>
          <Position.Body>
            <RunningLineLoader
              isActive={
                this.state.pricingErrorsStatus === "sorting" ||
                this.state.pricingErrorsStatus === "loading"
              }
            />
            <Table
              fullHeight
              columns={[
                { name: "dateRange", label: "Date", sortable: false },
                { name: "ottoPoNumber", label: "OTTO PO Number", sortable: true },
                { name: "invoiceNumber", label: "Invoice Number", sortable: true },
                { name: "productSku", label: "Product SKU", sortable: true },
                { name: "vendorName", label: "Vendor", sortable: true },
                { name: "ottoQty", label: "OTTO Qty", sortable: true, alignment: "right" },
                { name: "vendorQty", label: "Vendor Qty", sortable: true, alignment: "right" },
                { name: "ottoPrice", label: "OTTO Price", sortable: true, alignment: "right" },
                { name: "vendorPrice", label: "Vendor Price", sortable: true, alignment: "right" },
                { name: "toolbar", label: "", size: "md" }
              ]}
            >
              <Table.Header
                sortBy={this.state.sorting}
                onSortChanged={this.handleSortChanged}
              />
              <Table.Body>
                {isLoaded &&
                  (hasData ? (
                    this.state.pricingErrors.map((error, index) => (
                      <Table.Row
                        key={`${index}-${error.id}`}
                        cells={[
                          error.invoiceDate ? moment(error.invoiceDate).format("MM/DD/YYYY") : "N/A",
                          error.ottoPoNumber,
                          error.invoiceNumber || "N/A",
                          error.productSku,
                          error.vendorName || "N/A",
                          error.ottoQty !== null ? error.ottoQty : "N/A",
                          error.vendorQty !== null ? error.vendorQty : "N/A",
                          formatPrice(error.ottoPrice),
                          formatPrice(error.vendorPrice),
                          <Position.Toolbar key={`cell-toolbar-${index}-${error.id}`}>
                            <Button
                              onClick={() => this.handleResolveClick(error.id)}
                              disabled={this.state.isResolving[error.id]}
                              preloader={this.state.isResolving[error.id]}
                            >
                              Resolved
                            </Button>
                          </Position.Toolbar>
                        ]}
                      />
                    ))
                  ) : (
                    <Table.Row
                      key={0}
                      colSpan="10"
                      rows={this.props.perPage}
                      cells={[
                        <span key="0">
                          There are no errors within the parameters.
                        </span>
                      ]}
                    />
                  ))}
                {this.state.pricingErrorsStatus === "loading" &&
                  PricingErrorsList.renderPreloader(
                    this.props.perPage,
                    this.state.pricingErrorsStatus
                  )}
              </Table.Body>
            </Table>
          </Position.Body>
          <Pagination
            skip={this.state.skip}
            perPage={this.props.perPage}
            count={this.state.totalCount}
            onChange={this.handleChangePage}
          />
        </Position.Container>
      </MainContainer>
    );
  }
}

interface IConnectedProps {
  locationInfo?: IShortLocationInfo;
  locationOptions: IFlatLocationOption[];
  perPage: number;
}

interface IConnectedActions {
  setSearch: typeof actionsSearch.set;
  setPerPage: typeof layoutActions.setPerPage;
  notifySwagger: typeof notifySwagger;
  notify: typeof notify;
}

export default withRouter(
  connect(
    createStructuredSelector<IGlobalStore, IConnectedProps>({
      perPage: state => state.layout.perPage,
      locationInfo: state => state.currentUser.locationInfo,
      locationOptions: state => state.currentUser.locationOptions
    }),
    dispatch =>
      bindActionCreators(
        {
          setSearch: actionsSearch.set,
          setPerPage: layoutActions.setPerPage,
          notifySwagger,
          notify
        },
        dispatch
      )
  )(withGranted(PricingErrorsList))
);
